import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import {
  MoreVert as MoreVertIcon,
  KeyboardArrowUp as ArrowUp,
  KeyboardArrowDown as ArrowDown,
} from '@mui/icons-material';

import {
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableRow,
  TableFooter,
  TextField,
  IconButton,
  TablePagination,
  List,
  ListItem,
  Button,
  Checkbox,
  Popover,
  ClickAwayListener,
  ListItemIcon,
  ListItemText,
} from '@mui/material';

import { withStyles } from 'tss-react/mui';

import ExtraActionButtons from './components/ExtraActionButtons';

import './TableEnhanced.css';

const tableStyles = (theme, props) => ({
  clickableRow: {
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      cursor: 'pointer',
    },
    '&:hover > td': {
      color: `${theme.palette.primary.contrastText}!important`,
      backgroundColor: `${theme.palette.primary.main}!important`,
    },
  },
});

class ShowHideColumns extends PureComponent {
  render() {
    const { columns, toggleColumn, onClickShowHideColumns } = this.props;
    return (
      <div className="u-fx u-fx-align-center" data-qa="qa-table-hide-column">
        <IconButton
          size="large"
          aria-owns={this.props.openShowHideColumns ? 'show-hide-menu' : undefined}
          aria-haspopup="true"
          onClick={(event) => {
            onClickShowHideColumns(event);
          }}
        >
          <MoreVertIcon />
        </IconButton>
        <Popover
          id="show-hide-menu"
          open={this.props.openShowHideColumns}
          anchorEl={this.props.anchorElForShowHideColumns}
          anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
          targetOrigin={{ horizontal: 'left', vertical: 'top' }}
        >
          <ClickAwayListener onClickAway={() => this.props.onRequestCloseShowHideColumns()}>
            <List>
              {columns.map((col, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <Checkbox
                      disabled={col.disabled}
                      checked={col.visible}
                      onChange={() => toggleColumn(col.key)}
                    />
                  </ListItemIcon>
                  <ListItemText> {col.name} </ListItemText>
                </ListItem>
              ))}
            </List>
          </ClickAwayListener>
        </Popover>
        <p>Show/Hide columns</p>
      </div>
    );
  }
}

class ActionButtons extends PureComponent {
  render() {
    const { onClickCopy, onClickPrint, onClickExport, extraActionsButtons } = this.props;

    return (
      <div className="u-fx u-fx-center">
        <Button variant="contained" onClick={onClickCopy} data-qa="qa-table-copy" color="primary">
          Copy
        </Button>
        <Button
          variant="contained"
          onClick={onClickPrint}
          className="u-mh-full"
          label="Print"
          data-qa="qa-table-print"
          color="primary"
        >
          Print
        </Button>
        <Button
          variant="contained"
          onClick={() => onClickExport(false)}
          data-qa="qa-table-export"
          color="primary"
        >
          Export
        </Button>

        {extraActionsButtons !== null
          ? extraActionsButtons.map((config, index) => {
              if (config.extraActionsButtons && config.extraActionsButtons === true) {
                return (
                  <ExtraActionButtons
                    buttonList={config.extraActionsButtonList}
                    onReportItemEvent={this.props.onReportItemEvent}
                    exportCallback={onClickExport}
                  />
                );
              }
            })
          : null}
      </div>
    );
  }
}

const determineClass = (key) => {
  switch (key) {
    case 'actions':
      return 'table-actions';
    case 'markup':
      return 'table-markup';
    default:
      return '';
  }
};

const determineStyle = (col, el, value) => {
  if (value && col.customStyle != undefined && el[col.customStyle] != undefined) {
    let styleForSpecificValue = el[col.customStyle].find((cs) => {
      if (col.customStyleReferenceKey != undefined) {
        return el[col.customStyleReferenceKey].toLowerCase().includes(cs.Key);
      }
      return value.toLowerCase().includes(cs.Key);
    });

    if (styleForSpecificValue != undefined) {
      let cellStyle = JSON.parse(styleForSpecificValue.Style);
      return cellStyle;
    }
  }

  return col.style;
};

const determineBodyColContent = (
  el,
  col,
  elIndex,
  colIndex,
  defaultValueFormatter,
  onClickRow,
  customClasses
) => {
  switch (col.type) {
    case 'actions':
      return (
        <TableCell
          onClick={() => (onClickRow ? onClickRow(el, col) : {})}
          className="table-actions"
          hidden={!col.visible}
          key={colIndex}
          style={col.style}
        >
          {col.actions}
        </TableCell>
      );
    case 'markup':
      return (
        <TableCell
          onClick={() => (onClickRow ? onClickRow(el, col) : {})}
          className="table-markup"
          hidden={!col.visible}
          key={colIndex}
          style={col.style}
        >
          {col.markup(el, col, elIndex, colIndex)}
        </TableCell>
      );
    case 'html':
      return (
        <TableCell
          onClick={() => (onClickRow ? onClickRow(el, col) : {})}
          className="table-markup"
          hidden={!col.visible}
          key={colIndex}
          style={col.style}
        >
          <div style={col.style} dangerouslySetInnerHTML={{ __html: el[col['key']] }}></div>
        </TableCell>
      );
    default:
      return (
        <TableCell
          onClick={() => (onClickRow ? onClickRow(el, col) : {})}
          hidden={!col.visible}
          key={colIndex}
          style={determineStyle(col, el, el[col['key']])}
        >
          {defaultValueFormatter(el[col['key']], col.type)}
        </TableCell>
      );
  }
};

const constructTableBody = (
  defaultValueFormatter,
  data,
  onClickRow,
  columns,
  customClasses,
  onClickRowAction
) => {
  return (
    <TableBody className={'table-enhanced__body'} data-qa="qa-table-body">
      {data.length ? (
        data.map((el, elIndex) => (
          <TableRow
            key={elIndex}
            className={onClickRow && customClasses ? customClasses['clickableRow'] : ''}
          >
            {columns.map((col, colIndex) =>
              determineBodyColContent(
                el,
                col,
                elIndex,
                colIndex,
                defaultValueFormatter,
                onClickRow,
                customClasses
              )
            )}
          </TableRow>
        ))
      ) : (
        <TableRow>
          <TableCell colSpan={columns ? columns.length : 2}>
            <p className="no-data">No data to show</p>
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  );
};

const constructTableFooter = (footerData, columns, defaultValueFormatter) => {
  return (
    <TableRow data-qa="qa-table-footer">
      {columns.map((col, index) => (
        <TableCell className="u-bold" hidden={!col.visible} key={index} style={col.style}>
          {footerData[col.key] === 'Total'
            ? footerData[col.key]
            : defaultValueFormatter(footerData[col.key], col.type)}
        </TableCell>
      ))}
    </TableRow>
  );
};

const sumColumnWidth = (offset, col) => {
  if (!col) return offset;

  if (!col.visible) return offset;

  let colWidth = 10; // legacy default

  if (col.style && col.style.width && col.style.width.indexOf('rem') !== -1) {
    colWidth = parseInt(col.style.width.trim(), 10);
  }

  return offset + colWidth;
};

class TableEnhancedNotStyled extends PureComponent {
  render() {
    return (
      <div className="table-enhanced" data-qa={this.props.qaTag || 'qa-table'}>
        <div className="u-fx u-fx-align-center u-fx-space-between">
          {this.props.enableHideColumns ? (
            <ShowHideColumns
              columns={this.props.columns}
              toggleColumn={this.props.toggleColumn}
              openShowHideColumns={this.props.openShowHideColumns}
              anchorElForShowHideColumns={this.props.anchorElForShowHideColumns}
              onClickShowHideColumns={this.props.onClickShowHideColumns}
              onRequestCloseShowHideColumns={this.props.onRequestCloseShowHideColumns}
            />
          ) : null}
          {this.props.enableActionButtons ? (
            <ActionButtons
              onClickCopy={this.props.onClickCopy}
              onClickPrint={this.props.onClickPrint}
              onClickExport={this.props.onClickExport}
              extraActionsButtons={this.props.extraActionsButtons}
              onReportItemEvent={this.props.onReportItemEvent}
            />
          ) : null}
        </div>
        <div
          className={
            this.props.hasToPrint
              ? 'table-enhanced__wrapper table-enhanced__wrapper__print'
              : 'table-enhanced__wrapper'
          }
        >
          <div
            style={{ minWidth: `${this.props.columns.reduce(sumColumnWidth, 0)}rem` }}
            className="table-enhanced__wrapper__content"
          >
            <Table>
              <TableHead>
                <TableRow>
                  {this.props.columns.map((col, index) => (
                    <TableCell
                      className={determineClass(col.key)}
                      hidden={!col.visible}
                      key={index}
                      style={col.style}
                    >
                      <div className="u-fx u-fx-align-center">
                        {col.name !== ' ' ? <span className="u-mr-full">{col.name}</span> : null}
                        {this.props.filteredData.length && col.sortable ? (
                          <IconButton
                            size="large"
                            className="icon-sort-wrapper"
                            style={{ width: '1.5rem', height: '1.5rem', padding: '0' }}
                            onClick={() => this.props.onClickSortColumn(col.key, col.type)}
                          >
                            {this.props.sortColumn === col.key ? (
                              this.props.sortOrder === 'asc' ? (
                                <ArrowUp style={{ width: '.875rem', height: '.875rem' }} />
                              ) : (
                                <ArrowDown style={{ width: '.875rem', height: '.875rem' }} />
                              )
                            ) : (
                              <ArrowDown style={{ width: '.875rem', height: '.875rem' }} />
                            )}
                          </IconButton>
                        ) : null}
                      </div>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow className="table-filters">
                  {this.props.columns.map((col, index) => (
                    <TableCell hidden={!col.visible} key={index} style={col.style}>
                      {col.filterable ? (
                        <div>
                          <TextField
                            variant="standard"
                            style={{ width: '100%', fontSize: '.75rem' }}
                            placeholder={`Filter`}
                            onChange={(e) =>
                              this.props.onFilterColumn(col.key, col.type, e.target.value)
                            }
                          />
                        </div>
                      ) : null}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              {constructTableBody(
                this.props.defaultValueFormatter,
                this.props.data,
                this.props.onClickRow,
                this.props.columns,
                this.props.classes,
                this.props.onClickRowAction
              )}
              {this.props.hasFooter ? (
                <TableFooter
                  className={
                    this.props.hasToPrint
                      ? 'table-enhanced__footer table-enhanced__footer__print'
                      : 'table-enhanced__footer'
                  }
                >
                  {constructTableFooter(
                    this.props.footerData,
                    this.props.columns,
                    this.props.defaultValueFormatter
                  )}
                </TableFooter>
              ) : null}
            </Table>
          </div>
        </div>
        {this.props.filteredData.length ? (
          <div className="u-mt-full u-fx u-fx-align-center u-fx-space-between table-enhanced__pagination-wrapper">
            <div
              className="table-enhanced__items-per-page u-fx u-fx-align-center"
              style={{ width: '10rem' }}
            />
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Items per page:"
              component="div"
              count={this.props.totalItems}
              rowsPerPage={this.props.pageSize}
              page={this.props.activePage}
              backIconButtonProps={{
                'aria-label': 'Previous Page',
                'data-qa': 'qa-previous-table-page',
              }}
              nextIconButtonProps={{
                'aria-label': 'Next Page',
                'data-qa': 'qa-next-table-page',
              }}
              onPageChange={(event, page) => this.props.onClickChangePage(page)}
              onRowsPerPageChange={(e) => this.props.onChangePageSize(e.target.value)}
              SelectProps={{ 'data-qa': 'qa-table-item-no' }}
            />
          </div>
        ) : null}
        {this.props.hasToPrint ? (
          <div className="table-enhanced__print-wrapper" hidden>
            {constructTableBody(
              this.props.defaultValueFormatter,
              this.props.filteredData,
              this.props.onClickRow,
              this.props.columns
            )}
          </div>
        ) : null}
      </div>
    );
  }
}

TableEnhancedNotStyled.propTypes = {
  data: PropTypes.array,
  columns: PropTypes.array,
  footerData: PropTypes.object,
  toggleColumn: PropTypes.func,
  onClickCopy: PropTypes.func,
  onClickPrint: PropTypes.func,
  onClickExport: PropTypes.func,
  onReportItemEvent: PropTypes.func,
  activePage: PropTypes.number,
  numberOfPages: PropTypes.number,
  onClickChangePage: PropTypes.func,
  sortColumn: PropTypes.string,
  sortOrder: PropTypes.string,
  onClickSortColumn: PropTypes.func,
  onChangePageSize: PropTypes.func,
  onFilterColumn: PropTypes.func,
  hasFooter: PropTypes.bool,
  onClickRow: PropTypes.func,
  extraActionsButtons: PropTypes.array,
  dateTimeFromFilter: PropTypes.object,
  dateTimeToFilter: PropTypes.object,
};

TableEnhancedNotStyled.defaultProps = {
  data: [],
  columns: [],
  footerData: {},
  toggleColumn: () => {},
  onClickCopy: () => {},
  onClickPrint: () => {},
  onClickExport: () => {},
  onReportItemEvent: () => {},
  activePage: 0,
  numberOfPages: 1,
  onClickChangePage: () => {},
  sortColumn: '',
  sortOrder: 'asc',
  onClickSortColumn: () => {},
  onChangePageSize: () => {},
  onFilterColumn: () => {},
  hasFooter: false,
  footerContent: null,
  onClickRow: null,
  extraActionsButtons: [],
  dateTimeFromFilter: {},
  dateTimeToFilter: {},
};

const TableEnhanced = withStyles(TableEnhancedNotStyled, tableStyles);

export default TableEnhanced;
